/**
 * AddAnotherGrantDialog (Story 32.2 — Epic 32 Multi-Account UX)
 *
 * Confirm dialog for the "Add another [Platform] grant" affordance on the
 * connections tree. Explains the compartmentalization round-trip (you'll be
 * sent to the provider; switch logins there; attach accounts afterwards) and
 * launches a force-fresh OAuth flow on Continue — the server-side
 * `forceNewGrant` contract guarantees the existing grant is never overwritten.
 *
 * Design-directive compliance (§4.2 centered modal Dialog — NEVER a Sheet):
 * max-w-md, rounded-3xl, bg-black/35 overlay, 0.25s easeOut, motion stagger
 * (50ms increments) on inner content, Phosphor icons only, close blocked
 * while the auth-url fetch is pending.
 */

import { useEffect, useRef } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { motion, AnimatePresence } from "motion/react";
import { PlusCircle } from "@phosphor-icons/react";
import { Button } from "@provenleap/ui";

export interface AddAnotherGrantTarget {
  /** Platform key, e.g. "google-analytics-4". */
  platform: string;
  /** Platform display name, e.g. "Google Analytics 4". */
  displayName: string;
  /** OAuth provider/family display name, e.g. "Google". */
  providerName: string;
  /**
   * Whether the platform already holds ≥1 REAL grant (non-demo, token-backed).
   * Gates the compartmentalization copy (AC #1c/#2) — the affordance itself
   * shows either way.
   */
  hasExistingGrant: boolean;
}

interface AddAnotherGrantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: AddAnotherGrantTarget;
  /** True while the auth-url fetch is in flight — blocks close (§4.2 guard). */
  isPending: boolean;
  /** Launches `connect(platform, { forceNewGrant: true })` in the consumer. */
  onConfirm: () => void;
}

export function AddAnotherGrantDialog({
  open,
  onOpenChange,
  target,
  isPending,
  onConfirm,
}: AddAnotherGrantDialogProps) {
  // Block close while the auth-url fetch is pending — never lose in-flight
  // state (§4.2 handleOpenChange guard).
  function handleOpenChange(next: boolean): void {
    if (!next && isPending) return;
    onOpenChange(next);
  }

  // Exit-window guard (review R2): AnimatePresence freezes the EXITING
  // subtree with its last-seen props, so a Continue click during the ~250ms
  // exit animation of a cancelled dialog would fire a STALE onConfirm
  // closure and launch OAuth. A ref pierces the frozen closure — it always
  // reads the CURRENT open state.
  const openRef = useRef(open);
  useEffect(() => {
    openRef.current = open;
  }, [open]);

  function handleConfirm(): void {
    if (!openRef.current) return;
    onConfirm();
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-50 bg-black/35"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              />
            </DialogPrimitive.Overlay>

            <DialogPrimitive.Content asChild forceMount>
              <motion.div
                className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-3xl border-[0.5px] border-input bg-background p-8 shadow-lg max-h-[85vh] overflow-y-auto outline-none"
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              >
                <div className="flex flex-col space-y-1.5 text-left">
                  <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight flex items-center gap-2">
                    <PlusCircle size={19} className="text-primary shrink-0" />
                    Add another {target.displayName} grant
                  </DialogPrimitive.Title>
                  <DialogPrimitive.Description className="text-sm text-muted-foreground">
                    Authorize another {target.providerName} account for this
                    client.
                  </DialogPrimitive.Description>
                </div>

                {/* Sequential 50ms stagger (§6) — indices stay gapless when
                    the hasExistingGrant paragraph is absent. */}
                <div className="mt-6 space-y-4">
                  <motion.p
                    className="text-sm text-foreground"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 * 1 }}
                  >
                    You&apos;ll be sent to {target.displayName} to authorize
                    another account.
                    {target.hasExistingGrant && (
                      <>
                        {" "}
                        To keep client organizations compartmentalized, sign in
                        with (or switch to) the other login on{" "}
                        {target.providerName}&apos;s screen.
                      </>
                    )}{" "}
                    You can attach its accounts to clients afterwards.
                  </motion.p>

                  {target.hasExistingGrant && (
                    <motion.p
                      className="text-[13px] text-muted-foreground"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.05 * 2 }}
                    >
                      Your existing grant stays untouched — this always creates
                      a separate connection.
                    </motion.p>
                  )}

                  <motion.div
                    className="flex justify-end gap-3"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      delay: 0.05 * (target.hasExistingGrant ? 3 : 2),
                    }}
                  >
                    <Button
                      type="button"
                      variant="outline"
                      className="rounded-2xl bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all text-[13px]"
                      style={{ border: "0.5px solid var(--hairline)" }}
                      onClick={() => handleOpenChange(false)}
                      disabled={isPending}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      variant="brand"
                      className="rounded-2xl text-[13px] cursor-pointer"
                      onClick={handleConfirm}
                      disabled={isPending}
                    >
                      {isPending ? "Connecting…" : "Continue"}
                    </Button>
                  </motion.div>
                </div>
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}
